// ══════════════════════════════════════════════════════════════════════════════
// ABRA IA - Super Assistant IA pour Cockpit Particulier
// Layout 3 colonnes : Sidebar + Chat + Artifact Viewer (style Claude)
// ══════════════════════════════════════════════════════════════════════════════

import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Send, Sparkles, FileText, Download, Share2, Copy, Check,
  Mic, StopCircle, Loader2, ChevronRight, X, RefreshCw,
  Coins, AlertTriangle, Zap, PanelRightClose, PanelRightOpen,
  Receipt, Calendar, FileCode, Presentation, Image, MessageCircle,
  ShoppingBag, Wrench, Home, Search
} from 'lucide-react';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { API_URL } from '../../config/api';

// ══════════════════════════════════════════════════════════════════════════════
// CONSTANTES & STYLES
// ══════════════════════════════════════════════════════════════════════════════

const COLORS = {
  primary: '#1E3A5F',
  primaryLight: '#2d5a8a',
  accent: '#E8650A',
  accentHover: '#d45a09',
  background: '#f8fafc',
  surface: '#ffffff',
  surfaceHover: '#f1f5f9',
  border: '#e2e8f0',
  textPrimary: '#1e293b',
  textSecondary: '#64748b',
  textMuted: '#94a3b8',
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
};

// Suggestions de prompts pour les particuliers
const PROMPT_SUGGESTIONS = [
  { icon: Wrench, label: 'Trouver un artisan', prompt: 'Trouve-moi un plombier disponible cette semaine près de chez moi' },
  { icon: Receipt, label: 'Comparer des devis', prompt: 'Aide-moi à comparer 3 devis pour des travaux de peinture' },
  { icon: ShoppingBag, label: 'Rédiger une annonce', prompt: 'Rédige une annonce pour vendre mon ancien canapé' },
  { icon: Home, label: 'Estimer des travaux', prompt: 'Estime le coût de la rénovation de ma salle de bain' },
];

// Types d'artifacts supportés
const ARTIFACT_TYPES = {
  devis: { icon: Receipt, color: '#10b981', label: 'Devis' },
  annonce: { icon: ShoppingBag, color: '#3b82f6', label: 'Annonce' },
  document: { icon: FileText, color: '#8b5cf6', label: 'Document' },
  artisans: { icon: Wrench, color: '#f59e0b', label: 'Artisans' },
  estimation: { icon: Receipt, color: '#ec4899', label: 'Estimation' },
  comparatif: { icon: FileCode, color: '#64748b', label: 'Comparatif' },
};

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ══════════════════════════════════════════════════════════════════════════════

export default function AbraIAPage() {
  const { user } = useCurrentUser();
  
  // États
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [showArtifact, setShowArtifact] = useState(true);
  const [activeArtifact, setActiveArtifact] = useState(null);
  const [credits, setCredits] = useState({ balance: 0, loading: true });
  const [copiedId, setCopiedId] = useState(null);
  
  const chatContainerRef = useRef(null);
  const inputRef = useRef(null);
  
  // Charger le solde AbracaCoins
  useEffect(() => {
    const fetchCredits = async () => {
      try {
        const authToken = localStorage.getItem('buyer_token') || localStorage.getItem('ecosystem_token');
        if (!authToken) {
          setCredits({ balance: 500, loading: false }); // Démo
          return;
        }
        
        const res = await fetch(`${API_URL}/api/abracacoins/balance`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        
        if (res.ok) {
          const data = await res.json();
          setCredits({ balance: data.total_available || 0, loading: false });
        } else {
          setCredits({ balance: 500, loading: false }); // Fallback démo
        }
      } catch (e) {
        console.error('Erreur chargement crédits:', e);
        setCredits({ balance: 500, loading: false });
      }
    };
    
    fetchCredits();
  }, []);
  
  // Auto-scroll vers le bas
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);
  
  // Envoyer un message
  const handleSendMessage = async (customPrompt = null) => {
    const messageText = customPrompt || inputValue.trim();
    if (!messageText || isLoading) return;
    
    // Ajouter le message utilisateur
    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    
    try {
      const authToken = localStorage.getItem('buyer_token') || localStorage.getItem('ecosystem_token');
      
      const res = await fetch(`${API_URL}/api/ai/abra-chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
        },
        body: JSON.stringify({
          message: messageText,
          context: 'cockpit_particulier',
          user_id: user?.id || 'demo_user'
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        
        // Construire le message assistant
        const assistantMessage = {
          id: Date.now() + 1,
          role: 'assistant',
          content: data.response || data.message || "Voici ma réponse.",
          timestamp: new Date().toISOString(),
          artifact: data.artifact || null,
          credits_consumed: data.credits_consumed || 0
        };
        
        setMessages(prev => [...prev, assistantMessage]);
        
        // Mettre à jour les crédits
        if (data.credits_consumed) {
          setCredits(prev => ({ ...prev, balance: Math.max(0, prev.balance - data.credits_consumed) }));
        }
        
        // Ouvrir l'artifact si présent
        if (data.artifact) {
          setActiveArtifact(data.artifact);
          setShowArtifact(true);
        }
      } else {
        // Fallback : réponse simulée
        const fallbackResponse = generateFallbackResponse(messageText);
        setMessages(prev => [...prev, fallbackResponse]);
        
        if (fallbackResponse.artifact) {
          setActiveArtifact(fallbackResponse.artifact);
          setShowArtifact(true);
        }
      }
    } catch (error) {
      console.error('Erreur chat:', error);
      // Fallback en cas d'erreur
      const fallbackResponse = generateFallbackResponse(messageText);
      setMessages(prev => [...prev, fallbackResponse]);
      
      if (fallbackResponse.artifact) {
        setActiveArtifact(fallbackResponse.artifact);
        setShowArtifact(true);
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  // Générer une réponse de fallback (mode démo)
  const generateFallbackResponse = (userMsg) => {
    const msg = userMsg.toLowerCase();
    let artifact = null;
    let content = '';
    
    if (msg.includes('artisan') || msg.includes('plombier') || msg.includes('électricien') || msg.includes('carreleur')) {
      content = "J'ai trouvé plusieurs artisans certifiés près de chez toi. Voici les mieux notés :";
      artifact = {
        type: 'artisans',
        title: 'Artisans recommandés',
        subtitle: '3 résultats',
        preview: 'Artisans certifiés près de chez toi',
        content: generateArtisansContent()
      };
    } else if (msg.includes('devis') || msg.includes('compar')) {
      content = "Voici un comparatif des devis que tu m'as soumis. Je t'ai mis en évidence les points importants.";
      artifact = {
        type: 'comparatif',
        title: 'Comparatif Devis',
        subtitle: '3 devis analysés',
        preview: 'Analyse comparative',
        content: generateComparatifContent()
      };
    } else if (msg.includes('annonce') || msg.includes('vendre')) {
      content = "J'ai rédigé une annonce attractive pour toi. Tu peux la copier et la publier directement.";
      artifact = {
        type: 'annonce',
        title: 'Annonce rédigée',
        subtitle: 'Prête à publier',
        preview: 'Annonce optimisée',
        content: generateAnnonceContent()
      };
    } else if (msg.includes('estim') || msg.includes('coût') || msg.includes('prix') || msg.includes('budget')) {
      content = "Voici une estimation détaillée basée sur les prix du marché dans ta région.";
      artifact = {
        type: 'estimation',
        title: 'Estimation Travaux',
        subtitle: 'Prix indicatifs',
        preview: 'Budget: 4 500€ - 7 500€',
        content: generateEstimationContent()
      };
    } else {
      content = "Je suis là pour t'aider ! Je peux trouver des artisans, comparer des devis, rédiger des annonces, ou estimer le coût de tes travaux. Dis-moi ce dont tu as besoin.";
    }
    
    return {
      id: Date.now() + 1,
      role: 'assistant',
      content,
      timestamp: new Date().toISOString(),
      artifact,
      credits_consumed: artifact ? 3 : 1
    };
  };
  
  // Copier le contenu
  const handleCopy = async (content, id) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (e) {
      console.error('Erreur copie:', e);
    }
  };
  
  // Télécharger l'artifact
  const handleDownload = (artifact) => {
    const blob = new Blob([artifact.content || ''], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${artifact.title || 'document'}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };
  
  // Raccourci clavier Enter
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div 
      style={{
        display: 'flex',
        height: 'calc(100vh - 180px)',
        background: COLORS.background,
        borderRadius: '16px',
        overflow: 'hidden',
        border: `1px solid ${COLORS.border}`,
      }}
    >
      {/* ═══════════════════════════════════════════════════════════════════════
          COLONNE CHAT (Centre)
          ═══════════════════════════════════════════════════════════════════════ */}
      <div 
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          background: COLORS.surface,
          borderRight: showArtifact && activeArtifact ? `1px solid ${COLORS.border}` : 'none',
        }}
      >
        {/* Header Chat */}
        <div 
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 24px',
            borderBottom: `1px solid ${COLORS.border}`,
            background: COLORS.surface,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div 
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                background: `linear-gradient(135deg, ${COLORS.accent} 0%, ${COLORS.accentHover} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Sparkles size={20} color="#ffffff" />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: COLORS.textPrimary }}>
                Abra, ton assistant
              </h1>
              <p style={{ margin: 0, fontSize: '13px', color: COLORS.textSecondary }}>
                Aide pour tes projets et achats
              </p>
            </div>
          </div>
          
          {/* Badge AbracaCoins */}
          <div 
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 14px',
              borderRadius: '12px',
              background: credits.balance < 100 ? 'rgba(239, 68, 68, 0.1)' : 'rgba(232, 101, 10, 0.1)',
              border: `1px solid ${credits.balance < 100 ? 'rgba(239, 68, 68, 0.2)' : 'rgba(232, 101, 10, 0.2)'}`,
            }}
          >
            {credits.balance < 100 ? (
              <AlertTriangle size={16} color={COLORS.error} />
            ) : (
              <Coins size={16} color={COLORS.accent} />
            )}
            <span 
              style={{ 
                fontSize: '14px', 
                fontWeight: '600', 
                color: credits.balance < 100 ? COLORS.error : COLORS.accent 
              }}
            >
              {credits.loading ? '...' : credits.balance.toLocaleString()}
            </span>
            <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>AbracaCoins</span>
          </div>
        </div>
        
        {/* Zone de Chat */}
        <div 
          ref={chatContainerRef}
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '24px',
          }}
        >
          {messages.length === 0 ? (
            // État vide - Suggestions
            <div style={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: '32px',
            }}>
              <div style={{ textAlign: 'center' }}>
                <div 
                  style={{
                    width: '80px',
                    height: '80px',
                    margin: '0 auto 16px',
                    borderRadius: '20px',
                    background: `linear-gradient(135deg, ${COLORS.accent} 0%, ${COLORS.accentHover} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Sparkles size={36} color="#ffffff" />
                </div>
                <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: COLORS.textPrimary }}>
                  Bonjour{user?.name ? `, ${user.name.split(' ')[0]}` : ''} !
                </h2>
                <p style={{ margin: '8px 0 0', fontSize: '15px', color: COLORS.textSecondary, maxWidth: '400px' }}>
                  Je suis Abra, ton assistant personnel. Je t'aide à trouver des artisans, comparer des devis, rédiger des annonces et bien plus encore.
                </p>
              </div>
              
              {/* Grille de suggestions */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(2, 1fr)', 
                gap: '12px',
                width: '100%',
                maxWidth: '480px',
              }}>
                {PROMPT_SUGGESTIONS.map((suggestion, idx) => {
                  const Icon = suggestion.icon;
                  return (
                    <button
                      key={idx}
                      onClick={() => handleSendMessage(suggestion.prompt)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '14px 16px',
                        background: COLORS.surface,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '12px',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = COLORS.accent;
                        e.currentTarget.style.background = 'rgba(232, 101, 10, 0.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = COLORS.border;
                        e.currentTarget.style.background = COLORS.surface;
                      }}
                    >
                      <Icon size={20} color={COLORS.accent} />
                      <span style={{ fontSize: '14px', fontWeight: '500', color: COLORS.textPrimary }}>
                        {suggestion.label}
                      </span>
                      <ChevronRight size={16} color={COLORS.textMuted} style={{ marginLeft: 'auto' }} />
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            // Messages
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {messages.map((msg) => (
                <div 
                  key={msg.id}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  }}
                >
                  {/* Bulle de message */}
                  <div 
                    style={{
                      maxWidth: '80%',
                      padding: '14px 18px',
                      borderRadius: msg.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                      background: msg.role === 'user' ? COLORS.accent : COLORS.surfaceHover,
                      color: msg.role === 'user' ? '#ffffff' : COLORS.textPrimary,
                    }}
                  >
                    <p style={{ margin: 0, fontSize: '15px', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
                      {msg.content}
                    </p>
                  </div>
                  
                  {/* Artifact Card (pour les messages assistant) */}
                  {msg.role === 'assistant' && msg.artifact && (
                    <button
                      onClick={() => {
                        setActiveArtifact(msg.artifact);
                        setShowArtifact(true);
                      }}
                      style={{
                        marginTop: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 16px',
                        background: COLORS.surface,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '12px',
                        cursor: 'pointer',
                        maxWidth: '80%',
                        textAlign: 'left',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = COLORS.accent;
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = COLORS.border;
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      {(() => {
                        const artifactConfig = ARTIFACT_TYPES[msg.artifact.type] || ARTIFACT_TYPES.document;
                        const Icon = artifactConfig.icon;
                        return (
                          <>
                            <div 
                              style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '10px',
                                background: `${artifactConfig.color}15`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Icon size={20} color={artifactConfig.color} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: COLORS.textPrimary }}>
                                {msg.artifact.title}
                              </p>
                              <p style={{ margin: '2px 0 0', fontSize: '12px', color: COLORS.textSecondary }}>
                                {msg.artifact.preview || msg.artifact.subtitle}
                              </p>
                            </div>
                            <ChevronRight size={18} color={COLORS.textMuted} />
                          </>
                        );
                      })()}
                    </button>
                  )}
                  
                  {/* Actions pour les messages assistant */}
                  {msg.role === 'assistant' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '8px' }}>
                      <button
                        onClick={() => handleCopy(msg.content, msg.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '6px 10px',
                          background: 'transparent',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          color: COLORS.textMuted,
                          fontSize: '12px',
                          transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = COLORS.surfaceHover;
                          e.currentTarget.style.color = COLORS.textSecondary;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = COLORS.textMuted;
                        }}
                      >
                        {copiedId === msg.id ? <Check size={14} /> : <Copy size={14} />}
                        <span>{copiedId === msg.id ? 'Copié !' : 'Copier'}</span>
                      </button>
                      
                      {msg.credits_consumed > 0 && (
                        <span style={{ fontSize: '11px', color: COLORS.textMuted, marginLeft: '8px' }}>
                          -{msg.credits_consumed} AbracaCoins
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
              
              {/* Indicateur de chargement */}
              {isLoading && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div 
                    style={{
                      padding: '14px 18px',
                      borderRadius: '18px 18px 18px 4px',
                      background: COLORS.surfaceHover,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Loader2 size={16} color={COLORS.textMuted} style={{ animation: 'spin 1s linear infinite' }} />
                      <span style={{ fontSize: '14px', color: COLORS.textSecondary }}>Abra réfléchit...</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Zone de saisie */}
        <div 
          style={{
            padding: '16px 24px 24px',
            borderTop: `1px solid ${COLORS.border}`,
            background: COLORS.surface,
          }}
        >
          <div 
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: '12px',
              padding: '12px 16px',
              background: COLORS.surfaceHover,
              borderRadius: '16px',
              border: `1px solid ${COLORS.border}`,
            }}
          >
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Demande à Abra..."
              rows={1}
              style={{
                flex: 1,
                resize: 'none',
                border: 'none',
                background: 'transparent',
                fontSize: '15px',
                color: COLORS.textPrimary,
                outline: 'none',
                minHeight: '24px',
                maxHeight: '120px',
                lineHeight: '1.5',
              }}
            />
            
            {/* Bouton Micro */}
            <button
              onClick={() => setIsRecording(!isRecording)}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                border: 'none',
                background: isRecording ? COLORS.error : 'transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
              }}
            >
              {isRecording ? (
                <StopCircle size={20} color="#ffffff" />
              ) : (
                <Mic size={20} color={COLORS.textMuted} />
              )}
            </button>
            
            {/* Bouton Envoyer */}
            <button
              onClick={() => handleSendMessage()}
              disabled={!inputValue.trim() || isLoading}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                border: 'none',
                background: inputValue.trim() ? COLORS.accent : COLORS.border,
                cursor: inputValue.trim() ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
              }}
            >
              <Send size={18} color={inputValue.trim() ? '#ffffff' : COLORS.textMuted} />
            </button>
          </div>
          
          <p style={{ 
            margin: '8px 0 0', 
            fontSize: '11px', 
            color: COLORS.textMuted, 
            textAlign: 'center' 
          }}>
            Abra peut faire des erreurs. Vérifie les informations importantes.
          </p>
        </div>
      </div>
      
      {/* ═══════════════════════════════════════════════════════════════════════
          COLONNE ARTIFACT VIEWER (Droite)
          ═══════════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {showArtifact && activeArtifact && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: '45%', opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            style={{
              background: COLORS.surface,
              borderLeft: `1px solid ${COLORS.border}`,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Header Artifact */}
            <div 
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 20px',
                borderBottom: `1px solid ${COLORS.border}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {(() => {
                  const artifactConfig = ARTIFACT_TYPES[activeArtifact.type] || ARTIFACT_TYPES.document;
                  const Icon = artifactConfig.icon;
                  return (
                    <>
                      <div 
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '10px',
                          background: `${artifactConfig.color}15`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Icon size={18} color={artifactConfig.color} />
                      </div>
                      <div>
                        <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: COLORS.textPrimary }}>
                          {activeArtifact.title}
                        </h3>
                        <p style={{ margin: 0, fontSize: '12px', color: COLORS.textSecondary }}>
                          {activeArtifact.subtitle}
                        </p>
                      </div>
                    </>
                  );
                })()}
              </div>
              
              {/* Actions */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  onClick={() => handleCopy(activeArtifact.content, 'artifact')}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    border: `1px solid ${COLORS.border}`,
                    background: COLORS.surface,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title="Copier"
                >
                  {copiedId === 'artifact' ? <Check size={16} color={COLORS.success} /> : <Copy size={16} color={COLORS.textSecondary} />}
                </button>
                
                <button
                  onClick={() => handleDownload(activeArtifact)}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    border: `1px solid ${COLORS.border}`,
                    background: COLORS.surface,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title="Télécharger"
                >
                  <Download size={16} color={COLORS.textSecondary} />
                </button>
                
                <button
                  onClick={() => {
                    setShowArtifact(false);
                    setActiveArtifact(null);
                  }}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    border: `1px solid ${COLORS.border}`,
                    background: COLORS.surface,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title="Fermer"
                >
                  <X size={16} color={COLORS.textSecondary} />
                </button>
              </div>
            </div>
            
            {/* Contenu Artifact */}
            <div 
              style={{
                flex: 1,
                padding: '20px',
                overflowY: 'auto',
                background: '#fafafa',
              }}
            >
              <pre 
                style={{
                  margin: 0,
                  padding: '20px',
                  background: COLORS.surface,
                  borderRadius: '12px',
                  border: `1px solid ${COLORS.border}`,
                  fontSize: '13px',
                  lineHeight: '1.6',
                  color: COLORS.textPrimary,
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                }}
              >
                {activeArtifact.content}
              </pre>
            </div>
            
            {/* Footer Artifact avec actions */}
            <div 
              style={{
                padding: '16px 20px',
                borderTop: `1px solid ${COLORS.border}`,
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <button
                onClick={() => handleDownload(activeArtifact)}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '12px 16px',
                  background: COLORS.accent,
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = COLORS.accentHover;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = COLORS.accent;
                }}
              >
                <Download size={18} />
                Télécharger
              </button>
              
              <button
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '12px 16px',
                  background: COLORS.surface,
                  color: COLORS.textPrimary,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                <Share2 size={18} />
                Partager
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Bouton pour réouvrir l'artifact (si fermé) */}
      {!showArtifact && messages.some(m => m.artifact) && (
        <button
          onClick={() => {
            const lastArtifact = [...messages].reverse().find(m => m.artifact)?.artifact;
            if (lastArtifact) {
              setActiveArtifact(lastArtifact);
              setShowArtifact(true);
            }
          }}
          style={{
            position: 'absolute',
            right: '16px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            border: `1px solid ${COLORS.border}`,
            background: COLORS.surface,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
          title="Ouvrir le document"
        >
          <PanelRightOpen size={18} color={COLORS.textSecondary} />
        </button>
      )}
      
      {/* Style pour l'animation de rotation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// GÉNÉRATEURS DE CONTENU DÉMO
// ══════════════════════════════════════════════════════════════════════════════

function generateArtisansContent() {
  return `
═══════════════════════════════════════════════════════════
              ARTISANS RECOMMANDÉS PRÈS DE CHEZ TOI
═══════════════════════════════════════════════════════════

1. MARTIN PLOMBERIE
   ────────────────────────────────────────────────────────
   Métier    : Plombier chauffagiste
   Note      : ★★★★★ 4.9/5 (124 avis)
   Localité  : Paris 12e (2.3 km)
   Dispo     : Cette semaine ✓
   Certifié  : RGE, Qualibat ✓
   
   Spécialités :
   • Installation sanitaires
   • Débouchage urgent
   • Chauffe-eau / chaudière
   
   Contact : 06 12 34 56 78

2. SDB EXPRESS
   ────────────────────────────────────────────────────────
   Métier    : Spécialiste salle de bain
   Note      : ★★★★★ 4.8/5 (256 avis)
   Localité  : Paris 20e (3.1 km)
   Dispo     : Sous 48h ✓
   Certifié  : RGE, Artisan de confiance ✓
   
   Spécialités :
   • Douche italienne
   • Rénovation complète SDB
   • PMR / Accessibilité
   
   Contact : 06 23 45 67 89

3. DUPONT SERVICES
   ────────────────────────────────────────────────────────
   Métier    : Plombier
   Note      : ★★★★☆ 4.6/5 (89 avis)
   Localité  : Paris 11e (1.8 km)
   Dispo     : Semaine prochaine
   Certifié  : Qualibat ✓
   
   Spécialités :
   • Dépannage rapide
   • Petits travaux
   • Devis gratuit
   
   Contact : 06 34 56 78 90

═══════════════════════════════════════════════════════════
💡 CONSEIL ABRA
───────────────────────────────────────────────────────────
Pour ta salle de bain, je te recommande SDB Express.
Ils sont spécialisés et ont d'excellents avis pour les
rénovations complètes.

Veux-tu que je leur envoie une demande de devis ?
═══════════════════════════════════════════════════════════
`.trim();
}

function generateComparatifContent() {
  return `
═══════════════════════════════════════════════════════════
                 COMPARATIF DE TES 3 DEVIS
═══════════════════════════════════════════════════════════

                    Devis A     Devis B     Devis C
                    ──────────  ──────────  ──────────
TOTAL TTC           5 976 €     6 890 €     5 200 €
                    
Délai intervention  2 sem.      3 sem.      1 sem.
Garantie           10 ans      10 ans      2 ans ⚠️

DÉTAIL DES POSTES
──────────────────────────────────────────────────────────

Main d'œuvre        2 200 €     2 800 €     1 800 €
Fournitures         2 480 €     2 890 €     2 500 €
Dépose/évacuation     300 €       400 €       200 €
TVA (20%)             996 €       800 €       700 €

POINTS FORTS
──────────────────────────────────────────────────────────

Devis A ✓
• Bon rapport qualité/prix
• Matériaux de qualité (Grohe, Schulte)
• Garantie décennale

Devis B
• Finitions haut de gamme
• Carrelage premium inclus

Devis C ⚠️
• Prix attractif MAIS :
  - Garantie courte (2 ans seulement)
  - Marques inconnues
  - Délai trop court = risque qualité ?

═══════════════════════════════════════════════════════════
💡 RECOMMANDATION ABRA
───────────────────────────────────────────────────────────
Je te conseille le DEVIS A.

C'est le meilleur équilibre entre prix et qualité.
Le Devis C est moins cher mais la garantie de 2 ans
est insuffisante pour des travaux de salle de bain.

⚠️ Vérifie que l'artisan a bien une assurance décennale !
═══════════════════════════════════════════════════════════
`.trim();
}

function generateAnnonceContent() {
  return `
═══════════════════════════════════════════════════════════
              ANNONCE PRÊTE À PUBLIER
═══════════════════════════════════════════════════════════

TITRE (optimisé pour la recherche)
──────────────────────────────────────────────────────────
Canapé 3 places cuir véritable - Excellent état - Paris 12

DESCRIPTION
──────────────────────────────────────────────────────────

Magnifique canapé 3 places en cuir véritable, couleur 
cognac.

Dimensions : 220 x 95 x 85 cm (L x P x H)

État : Excellent - Utilisé avec soin dans un salon 
non-fumeur. Quelques légères marques d'usure sur 
les accoudoirs, normales pour un canapé de cet âge.

Caractéristiques :
• Cuir pleine fleur véritable
• Structure bois massif
• Assise confortable et profonde
• Facile à nettoyer

Marque : Roche Bobois
Acheté neuf : 3 500 €
Prix de vente : 850 € (négociable)

À venir chercher sur place (Paris 12e).
Possibilité d'aide au chargement.

📞 Contact par message sur l'application.

═══════════════════════════════════════════════════════════
💡 CONSEILS POUR VENDRE RAPIDEMENT
───────────────────────────────────────────────────────────
• Ajoute 4-5 photos (vue d'ensemble + détails)
• Réponds vite aux messages (idéalement < 1h)
• Prix légèrement négociable = plus d'intéressés
• Mentionne "vendu" quand c'est fait !
═══════════════════════════════════════════════════════════
`.trim();
}

function generateEstimationContent() {
  return `
═══════════════════════════════════════════════════════════
          ESTIMATION TRAVAUX - Rénovation Salle de Bain
═══════════════════════════════════════════════════════════

Surface estimée : 5 m² (salle de bain standard)
Localisation : Paris / Île-de-France

ESTIMATION BASSE          ESTIMATION HAUTE
      4 500 €         →         7 500 €
        │                         │
        └─────────────────────────┘
              Fourchette de prix

DÉTAIL PAR POSTE
──────────────────────────────────────────────────────────

                          Mini        Maxi
Démolition / Dépose       200 €       400 €
Plomberie                 500 €     1 200 €
Électricité               200 €       500 €
Carrelage sol (m²)         50 €        90 €
Carrelage mur (m²)         40 €        80 €
Douche italienne        1 500 €     3 000 €
Meuble vasque             300 €     1 000 €
WC suspendu               400 €       800 €
Main d'œuvre            1 500 €     2 500 €

FACTEURS QUI INFLUENCENT LE PRIX
──────────────────────────────────────────────────────────

↑ Prix plus élevé si :
• Carrelage grand format / haut de gamme
• Robinetterie encastrée
• Douche XXL ou balnéo
• Travaux sur murs porteurs
• Déplacement des évacuations

↓ Prix plus bas si :
• Conservation des évacuations actuelles
• Carrelage entrée de gamme
• Équipements standards
• Chantier facile d'accès

═══════════════════════════════════════════════════════════
💡 CONSEIL ABRA
───────────────────────────────────────────────────────────
Pour avoir une estimation précise, je te conseille de
demander 3 devis à des artisans locaux.

Veux-tu que je te trouve des plombiers-carreleurs
disponibles cette semaine ?
═══════════════════════════════════════════════════════════
`.trim();
}
